import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { sectionVariant, staggerContainer, heroVariant, viewportOnce, glowVariant } from "@/lib/motion";
import { ConsultationForm } from "@/components/forms";
import { usePageContent } from "@/hooks/usePageContent";

const fallback = {
  label: "Contact",
  headline: "Let's start with a conversation.",
  subheadline: "No pitch decks. No pressure. Just a genuine discussion about where you are and where you want to be.",
  email: "team@xperfomax.com",
  location: "India",
  responseTime: "Within 1 business day",
  trustText: "We respond to every inquiry personally. No automated responses, no sales team gatekeeping. If you reach out, you will hear from someone who can actually help.",
  formTitle: "Start a conversation",
  formDescription: "Tell us about your growth challenges. We will respond within one business day.",
};

const Contact = () => {
  const { content } = usePageContent("contact", fallback);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* HERO */}
      <section className="relative pt-20 pb-6 md:pt-32 md:pb-12 lg:pt-44 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <motion.div 
            className="absolute top-0 right-1/4 w-[800px] h-[800px] rounded-full"
            initial="hidden"
            animate="visible"
            variants={glowVariant}
            style={{
              background: 'radial-gradient(circle, hsl(var(--primary) / 0.06) 0%, transparent 70%)',
            }}
          />
        </div>

        <div className="container-wide relative z-10">
          <div className="max-w-3xl">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <motion.span 
                variants={heroVariant}
                className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block"
              >
                {content.label}
              </motion.span>
              <motion.h1 
                variants={heroVariant}
                className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground mb-6 leading-[1.1]"
              >
                {content.headline}
              </motion.h1>
              <motion.p 
                variants={heroVariant}
                className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl"
              >
                {content.subheadline}
              </motion.p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CONTACT SECTION */}
      <section className="py-10 md:py-16 lg:py-24 border-t border-border">
        <div className="container-wide">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-24">
            {/* Left - Contact Info */}
            <motion.div
              className="lg:col-span-4"
              initial="hidden"
              whileInView="visible"
              viewport={viewportOnce}
              variants={sectionVariant}
            >
              <div className="space-y-8 lg:sticky lg:top-32">
                <div>
                  <span className="text-xs uppercase tracking-wider font-medium text-muted-foreground block mb-2">
                    Email
                  </span>
                  <a 
                    href={`mailto:${content.email}`} 
                    className="text-foreground hover:text-primary transition-colors text-lg"
                  >
                    {content.email}
                  </a>
                </div>
                <div>
                  <span className="text-xs uppercase tracking-wider font-medium text-muted-foreground block mb-2">
                    Location
                  </span>
                  <span className="text-foreground text-lg">{content.location}</span>
                </div>
                <div>
                  <span className="text-xs uppercase tracking-wider font-medium text-muted-foreground block mb-2">
                    Response Time
                  </span>
                  <span className="text-foreground text-lg font-medium">{content.responseTime}</span>
                </div>

                <div className="pt-8 border-t border-border">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {content.trustText}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Right - Form */}
            <div className="lg:col-span-8">
              <ConsultationForm 
                title={content.formTitle}
                description={content.formDescription}
                showCalendly={true}
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
